import { logger } from '@/lib/logger'
import {
  getRuntimePublicConfig,
  setRuntimePublicConfig,
} from '@/src/lib/runtime-public-config'
import type { RuntimePublicConfig } from '@/src/lib/runtime-public-config.shared'

type RuntimeConfigApiResponse = {
  success?: boolean
  config?: Record<string, string>
}

let runtimeConfigBootstrapPromise: Promise<void> | null = null

function hasMapsApiKey(config: RuntimePublicConfig): boolean {
  const key = config.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY
  return typeof key === 'string' && key.trim().length > 0
}

/**
 * Hydrates runtime public config on-demand for template/map previews.
 * Uses a shared in-flight promise to avoid duplicate /api/runtime-config fetches.
 */
export async function ensureRuntimeConfigWithMapsKey(): Promise<void> {
  const currentConfig = getRuntimePublicConfig()
  if (hasMapsApiKey(currentConfig)) return

  if (!runtimeConfigBootstrapPromise) {
    runtimeConfigBootstrapPromise = (async () => {
      try {
        const response = await fetch('/api/runtime-config', {
          method: 'GET',
          cache: 'no-store',
        })
        if (!response.ok) {
          throw new Error(`Runtime config fetch failed (${response.status})`)
        }

        const payload = (await response.json()) as RuntimeConfigApiResponse
        if (!payload.success || !payload.config) return

        setRuntimePublicConfig({
          ...getRuntimePublicConfig(),
          ...payload.config,
        })
      } catch (error) {
        logger.warn(
          '[RuntimeConfigBootstrap] Failed to refresh runtime public config',
          error
        )
      } finally {
        runtimeConfigBootstrapPromise = null
      }
    })()
  }

  await runtimeConfigBootstrapPromise
}
